import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Select, { FormatOptionLabelMeta } from "react-select";

import {
  getSchoolSelectorStyles,
  SchoolSelectOption,
} from "components/global/select-field-styles";
import {
  SchoolSelectorControl,
  SchoolSelectorEmptyMessage,
  SchoolSelectorField,
  SchoolSelectorSelectWrap,
} from "components/global/styles/SchoolSelector.inline.styles";
import { workspaceApi } from "pages/app/workspace/services";

const ALL_PLAYERS_VALUE = "__all_players__";

const formatPlayerOptionLabel = (
  option: SchoolSelectOption,
  { context }: FormatOptionLabelMeta<SchoolSelectOption>,
) => {
  if (context === "value") {
    return option.label;
  }

  return option.menuLabel || option.label;
};

type PlayerSelectorProps = {
  playerId: string;
  schoolId: string;
};

export const PlayerSelector: React.FC<PlayerSelectorProps> = ({ playerId, schoolId }) => {
  const history = useHistory();
  const location = useLocation();
  const playersQuery = useQuery({
    queryKey: ["workspace-school-players", schoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(schoolId),
    enabled: Boolean(schoolId),
  });

  const players = useMemo(() => playersQuery.data || [], [playersQuery.data]);

  const playerOptions = useMemo<SchoolSelectOption[]>(() => {
    const options = players.map((player: any) => {
      const name = player.full_name || "Untitled player";
      const detail = [player.position, player.school_grade].filter(Boolean).join(" · ");

      return {
        label: name,
        menuLabel: detail ? `${name} · ${detail}` : name,
        value: String(player.id),
      };
    });

    return [{ label: "All players", value: ALL_PLAYERS_VALUE }, ...options];
  }, [players]);

  const selectedOption = useMemo(
    () => playerOptions.find((option) => option.value === String(playerId)) || null,
    [playerId, playerOptions],
  );

  const selectStyles = useMemo(() => getSchoolSelectorStyles(), []);
  const showEmptyState = !playersQuery.isLoading && Boolean(schoolId) && players.length === 0;

  const handlePlayerChange = (nextPlayerId: string) => {
    if (!nextPlayerId || nextPlayerId === ALL_PLAYERS_VALUE) {
      history.push("/workspace/school-players");
      return;
    }

    if (nextPlayerId === String(playerId)) {
      return;
    }

    history.push({
      pathname: `/workspace/school-players/${schoolId}/${nextPlayerId}`,
      search: location.search,
      state: {
        backTo: "/workspace/school-players",
      },
    });
  };

  if (!schoolId) {
    return null;
  }

  if (showEmptyState) {
    return <SchoolSelectorEmptyMessage>No players available</SchoolSelectorEmptyMessage>;
  }

  return (
    <SchoolSelectorField>
      <SchoolSelectorControl $compact>
        <SchoolSelectorSelectWrap>
          <Select
            aria-label="Select player"
            classNamePrefix="player-selector"
            formatOptionLabel={formatPlayerOptionLabel}
            inputId="player-selector"
            isClearable={false}
            isDisabled={playersQuery.isLoading}
            isSearchable={playerOptions.length > 6}
            menuPlacement="auto"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            onChange={(option) => handlePlayerChange(option?.value ?? "")}
            options={playerOptions}
            placeholder={playersQuery.isLoading ? "Loading players..." : "Select a player"}
            styles={selectStyles}
            value={selectedOption}
          />
        </SchoolSelectorSelectWrap>
      </SchoolSelectorControl>
    </SchoolSelectorField>
  );
};
